import {
  reactive, computed, watch,
} from 'vue';

import { useHead } from '@vueuse/head';
import { useRoute } from 'vue-router';
import useRequest from '@/hooks/useRequest';
import { useSiteStore } from '@/store/site';
import { getMetaAPI } from '@/api/meta';
import imageSource from '@/utils/imageSource';

const defaultMeta = () => {
  const defaultMetaInfo = {
    title: '台灣早產兒基金會 | Premature Baby Foundation of Taiwan.',
    description: '早產兒發生率持續上升，每年仍有將近1.7萬名早產兒來到這個世上，事實上，只要有完整的醫療照顧，早產兒已有很高的治癒率，只要給他機會，他就可以如你我般健康長大。馬偕紀念醫院及民間企業團體，於民國八十一年元月，共同發起成立「台灣早產兒基金會」。早產兒基金會透過各界的協助，為早產兒及其家庭點燃信心與希望；同時也藉由各種活動，推廣正確的觀念與知識，以預防早產的發生。',
    image: 'https://api.pbf.org.tw/uploads/site_seo/56abb78d4531b6674feb603b72e622b4.jpg',
  };

  const {
    title,
    description,
    image,
  } = defaultMetaInfo;

  return {
    title,
    description,
    metas: {
      og: {
        description,
        image,
        title,
      },
      line: {
        description,
        image,
        title,
      },
    },
  };
};

export default () => {
  const route = useRoute();
  const siteStore = useSiteStore();
  const siteData = reactive({
    title: '台灣早產兒基金會 | Premature Baby Foundation of Taiwan.',
    description: '',
  });
  const metaInfo = reactive([]);

  useHead({
    // Can be static or computed
    title: computed(() => siteData.title),
    meta: metaInfo,
  });

  const getMetaData = (seoData) => {
    const paths = Object.keys(seoData);

    for (let i = 0; i < paths.length; i += 1) {
      if (route.path === paths[i]) {
        return seoData[paths[i]];
      }
    }

    return false;
  };

  const setMetaData = () => {
    const { seo } = siteStore;

    let metaData = getMetaData(seo);

    if (!metaData) {
      metaData = defaultMeta();
    }

    siteData.title = `${metaData.title} | 台灣早產兒基金會 | Premature Baby Foundation of Taiwan.`;
    metaInfo.push({
      name: 'description',
      content: metaData.description,
    });

    if (metaData.metas) {
      const { og, line } = metaData.metas;

      if (og) {
        const ogKeys = Object.keys(og);

        ogKeys.forEach((key) => {
          if (og[key]) {
            if (key === 'image') {
              metaInfo.push({
                property: `og:${key}`,
                content: imageSource(og[key]),
              });
            } else {
              metaInfo.push({
                property: `og:${key}`,
                content: og[key],
              });
            }
          }
        });
      }

      if (line) {
        const lineKeys = Object.keys(line);

        lineKeys.forEach((key) => {
          if (line[key]) {
            if (key === 'image') {
              metaInfo.push({
                property: `line:${key}`,
                content: imageSource(line[key]) || '',
              });
            } else {
              metaInfo.push({
                property: `line:${key}`,
                content: line[key] || '',
              });
            }
          }
        });
      }
    }
  };

  const { request: metaRequset, response } = useRequest(getMetaAPI);

  const handleMetaRequest = () => {
    const { seo } = siteStore;

    if (Object.keys(seo).length) {
      setMetaData();
      return;
    }

    metaRequset();
  };

  watch(response, () => {
    const { status, result } = response.value;

    if (status) {
      siteStore.saveSeo(result.seo);

      result.meta.forEach((item) => {
        metaInfo.push({
          name: item.name,
          content: item.content,
        });
      });

      setMetaData();
    }
  }, {
    immediate: true,
  });

  watch(() => route.path, () => {
    setMetaData();
  });

  return {
    metaRequset: handleMetaRequest,

  };
};
